import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { COUNTRIES, currencyForCountry } from '../utils/countries';
import FindDuplicatesButton from '../components/FindDuplicatesButton';

const blankSupplier = {
  name: '', contact_person: '', email: '', phone: '', address: '',
  country: 'Bahrain', currency: 'BHD', payment_terms: '',
  bank_name: '', account_number: '', vat_number: '', cr_number: '',
  notes: '',
};

const CURRENCY_OPTIONS = ['BHD', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'INR'];

export default function Suppliers() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankSupplier);
  const [q, setQ] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');

  const canCreate = hasPermission(user, 'suppliers', 'create');
  const canEdit = hasPermission(user, 'suppliers', 'edit');
  const canDelete = hasPermission(user, 'suppliers', 'delete');

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await api.get('/suppliers');
      setList(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/suppliers/${editingId}`, form);
      else await api.post('/suppliers', form);
      setShowForm(false);
      setEditingId(null);
      setForm(blankSupplier);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({ ...blankSupplier, ...s });
    setShowForm(true);
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = list.filter((s) => {
    if (filterCountry !== 'all' && (s.country || '') !== filterCountry) return false;
    if (filterCurrency !== 'all' && (s.currency || '') !== filterCurrency) return false;
    if (!q) return true;
    return (s.name || '').toLowerCase().includes(q.toLowerCase())
        || (s.country || '').toLowerCase().includes(q.toLowerCase())
        || (s.contact_person || '').toLowerCase().includes(q.toLowerCase());
  });
  const supplierCountries = Array.from(new Set(list.map(s => s.country).filter(Boolean))).sort();
  const supplierCurrencies = Array.from(new Set(list.map(s => s.currency).filter(Boolean))).sort();

  return (
    <div className="p-4 space-y-4">
      <PageHeader title="Suppliers" subtitle="Manage vendors and supplier accounts" />

      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search supplier name / country / contact..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="border px-3 py-2 rounded flex-1 min-w-[240px]"
          data-testid="supplier-search"
        />
        <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="supplier-filter-country">
          <option value="all">All countries</option>
          {supplierCountries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="supplier-filter-currency">
          <option value="all">All currencies</option>
          {supplierCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {(q || filterCountry !== 'all' || filterCurrency !== 'all') && (
          <button
            type="button"
            onClick={() => { setQ(''); setFilterCountry('all'); setFilterCurrency('all'); }}
            className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            data-testid="supplier-clear-filters-btn"
          >
            Clear filters
          </button>
        )}
        {canCreate && (
          <button
            onClick={() => { setEditingId(null); setForm(blankSupplier); setShowForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium"
            data-testid="add-supplier-btn"
          >
            + Add Supplier
          </button>
        )}
        <FindDuplicatesButton
          module="suppliers"
          labelFields={['name', 'email', 'phone', 'country', 'currency']}
          onAfterDelete={fetchList}
        />
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Phone</th>
              <th className="px-3 py-2 text-left">Country</th>
              <th className="px-3 py-2 text-left">Currency</th>
              <th className="px-3 py-2 text-left">Payment Terms</th>
              {(canEdit || canDelete) && <th className="px-3 py-2 text-left">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={(canEdit || canDelete) ? 7 : 6} className="text-center py-6 text-gray-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={(canEdit || canDelete) ? 7 : 6} className="text-center py-6 text-gray-500">No suppliers</td></tr>
            ) : filtered.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2">{s.contact_person || '-'}</td>
                <td className="px-3 py-2">{s.phone || '-'}</td>
                <td className="px-3 py-2">{s.country || '-'}</td>
                <td className="px-3 py-2">{s.currency || '-'}</td>
                <td className="px-3 py-2">{s.payment_terms || '-'}</td>
                {(canEdit || canDelete) && (
                  <td className="px-3 py-2 whitespace-nowrap">
                    {canEdit && (
                      <button onClick={() => handleEdit(s)} className="text-blue-600 hover:text-blue-800 text-sm mr-3" data-testid={`edit-supplier-${s.id}`}>✏️ Edit</button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete(s)} className="text-red-600 hover:text-red-800 text-sm" data-testid={`delete-supplier-${s.id}`}>🗑 Delete</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="supplier-form-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">{editingId ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Name *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2" data-testid="supplier-name-input" />
              </div>
              <div>
                <label className="block text-sm mb-1">Contact Person</label>
                <input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm mb-1">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm mb-1">Country</label>
                <select
                  value={form.country}
                  onChange={(e) => {
                    const country = e.target.value;
                    // Auto-fill currency from the chosen country (admin can still override below)
                    setForm({ ...form, country, currency: currencyForCountry(country) });
                  }}
                  className="w-full border rounded px-3 py-2 bg-white"
                  data-testid="supplier-country-select"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full border rounded px-3 py-2 bg-white"
                  data-testid="supplier-currency-select"
                >
                  {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Auto-filled from country — edit only if needed.</p>
              </div>
              <div>
                <label className="block text-sm mb-1">Payment Terms</label>
                <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="w-full border rounded px-3 py-2" placeholder="e.g. 30 days credit" />
              </div>
              <div className="md:col-span-2 mt-2 pt-3 border-t">
                <h3 className="text-sm font-semibold text-gray-700">Bank & Tax</h3>
                <p className="text-xs text-gray-500">Shown on Purchase Orders so accounts can pay this supplier directly.</p>
              </div>
              <div>
                <label className="block text-sm mb-1">Bank Name</label>
                <input
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. HSBC Middle East"
                  data-testid="supplier-bank-name"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Account Number / IBAN</label>
                <input
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. BH00 HSBC 1234 5678 90"
                  data-testid="supplier-account-number"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">VAT Number</label>
                <input
                  value={form.vat_number}
                  onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. 200000000123456"
                  data-testid="supplier-vat-number"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">CR No.</label>
                <input
                  value={form.cr_number}
                  onChange={(e) => setForm({ ...form, cr_number: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Commercial Registration No."
                  data-testid="supplier-cr-number"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Address</label>
                <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded" data-testid="supplier-save-btn">{editingId ? 'Save Changes' : 'Create Supplier'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
